import {
  collection, doc, setDoc, getDoc, getDocs, query, where,
  onSnapshot, updateDoc, addDoc, serverTimestamp, orderBy,
  limit, runTransaction,
} from 'firebase/firestore'
import { db } from './firebase'
import { authHeaders } from './auth'
import { FUNCTIONS_BASE_URL } from './functionsBase'
import type {
  SessionMetadata, Participant, Message, Artifact,
  Reflection, Stage, AgentName, Feedback,
} from '../types'

export async function getSession(sessionId: string): Promise<SessionMetadata | null> {
  const snap = await getDoc(doc(db, 'sessions', sessionId))
  return snap.exists() ? (snap.data() as SessionMetadata) : null
}

export async function getUserSessions(uid: string) {
  // Queried on participantUids because that is the field the read rule checks,
  // Firestore validates a list query against the rule, not against the documents.
  const q = query(
    collection(db, 'sessions'),
    where('participantUids', 'array-contains', uid),
    orderBy('createdAt', 'desc'),
  )
  const snap = await getDocs(q)
  return snap.docs.map(d => ({ id: d.id, ...d.data() as SessionMetadata }))
}

/**
 * Sessions are created and joined server-side: that is where the room password is
 * checked and the quota is enforced, and it is what records the caller's uid on the
 * session so Firestore rules can scope access to real participants.
 */
async function sessionCall<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}))
    // Two shapes come back: a machine-readable code with prose alongside it
    // (`{error: 'SIGN_IN_REQUIRED', message}`), and quota errors where the prose
    // IS the error field. Branch on `code`, show `message`.
    const code: string = detail.error || 'REQUEST_FAILED'
    const message: string = detail.message || detail.error || 'Request failed'
    throw new SessionError(message, response.status, code)
  }
  return response.json()
}

export class SessionError extends Error {
  status: number
  /** Machine-readable error code, e.g. SIGN_IN_REQUIRED. Branch on this, not on the prose. */
  code: string
  constructor(message: string, status: number, code = 'REQUEST_FAILED') {
    super(message)
    this.status = status
    this.code = code
  }
}

export async function createSession(
  sessionName: string,
  password: string,
  creatorName: string,
  _creatorUid?: string,
  organizationId?: string,
  isSolo?: boolean,
): Promise<{ sessionId: string; roomCode: string; participantId: string }> {
  return sessionCall('createSessionFn', {
    sessionName,
    password,
    displayName: creatorName,
    organizationId,
    isSolo,
  })
}

/**
 * Solo sessions are the front door: free, but still tied to a signed-in account with
 * a verified email, so the server takes only a name. Everything else (room code,
 * password) is meaningless for a session with one person in it.
 */
export async function createSoloSession(
  displayName: string,
  sessionName?: string,
): Promise<{ sessionId: string; participantId: string; roomCode: string }> {
  // sessionName is optional: the server names it after the participant.
  return sessionCall('createSessionFn', { displayName, isSolo: true, sessionName })
}

// The hosting rewrite maps /api/emailSessionResults onto the emailSessionResultsFn
// function, so the public path and the function name differ. In dev we call the
// function directly and have to use its real name.
const EMAIL_RESULTS_PATH = import.meta.env.PROD ? 'emailSessionResults' : 'emailSessionResultsFn'

/** Send the post-session summary email for a completed session to any address. */
export async function emailSessionResults(
  sessionId: string,
  email: string,
  name?: string,
): Promise<void> {
  await sessionCall<{ sent: true }>(EMAIL_RESULTS_PATH, { sessionId, email, name })
}

export async function joinSession(
  roomCode: string,
  password: string,
  displayName: string,
): Promise<{ sessionId: string; participantId: string; displayName: string } | null> {
  try {
    return await sessionCall('joinSessionFn', { roomCode, password, displayName })
  } catch (err) {
    if (err instanceof SessionError && err.status === 403) return null
    throw err
  }
}


export async function getParticipantByUid(
  sessionId: string,
  uid: string,
): Promise<{ participantId: string; displayName: string } | null> {
  const q = query(
    collection(db, 'sessions', sessionId, 'participants'),
    where('uid', '==', uid),
    limit(1),
  )
  const snap = await getDocs(q)
  if (snap.empty) return null
  const doc = snap.docs[0]
  return { participantId: doc.id, displayName: doc.data().displayName }
}

export function subscribeToSession(
  sessionId: string,
  callback: (data: SessionMetadata) => void,
  onError?: (error: Error) => void,
) {
  return onSnapshot(doc(db, 'sessions', sessionId), (snap) => {
    if (snap.exists()) callback({ ...snap.data() } as SessionMetadata)
  }, (error) => {
    if (onError) onError(error as Error)
  })
}

export function subscribeToParticipants(
  sessionId: string,
  callback: (participants: Participant[]) => void,
) {
  return onSnapshot(
    collection(db, 'sessions', sessionId, 'participants'),
    (snap) => {
      const participants = snap.docs.map(d => ({ id: d.id, ...d.data() } as Participant))
      callback(participants)
    },
  )
}

export function subscribeToMessages(
  sessionId: string,
  callback: (messages: Message[]) => void,
) {
  return onSnapshot(
    query(
      collection(db, 'sessions', sessionId, 'messages'),
      orderBy('timestamp', 'asc'),
    ),
    (snap) => {
      const messages = snap.docs.map(d => ({ id: d.id, ...d.data() } as Message))
      // A doc written from this client has a null timestamp until the server commits it,
      // and Firestore sorts nulls first. Re-sort locally so a message we just sent stays
      // at the bottom instead of jumping to the top of the list for a moment. Agent
      // messages are written server-side, so their timestamp is always committed.
      messages.sort((a, b) => {
        const at = a.timestamp?.toMillis ? a.timestamp.toMillis() : Number.MAX_SAFE_INTEGER
        const bt = b.timestamp?.toMillis ? b.timestamp.toMillis() : Number.MAX_SAFE_INTEGER
        return at - bt
      })
      callback(messages)
    },
  )
}

export async function sendMessage(
  sessionId: string,
  participantId: string,
  displayName: string,
  content: string,
  type: Message['type'] = 'message',
  agentName?: AgentName,
): Promise<string> {
  const ref = await addDoc(collection(db, 'sessions', sessionId, 'messages'), {
    participantId,
    agentName: agentName || null,
    displayName,
    content,
    type,
    timestamp: serverTimestamp(),
    visible: true,
  })
  return ref.id
}

export async function updateReflectionSynthesis(
  sessionId: string,
  reflectionId: string,
  synthesisMessageId: string,
) {
  await updateDoc(
    doc(db, 'sessions', sessionId, 'reflections', reflectionId),
    { synthesisMessageId },
  )
}

export async function updateTyping(
  sessionId: string,
  participantId: string,
  isTyping: boolean,
) {
  await updateDoc(
    doc(db, 'sessions', sessionId, 'participants', participantId),
    { isTyping },
  )
}

export async function markIntroSent(sessionId: string): Promise<void> {
  const ref = doc(db, 'sessions', sessionId)
  await updateDoc(ref, { introSent: true })
}

export async function startSession(sessionId: string) {
  await updateDoc(doc(db, 'sessions', sessionId), { status: 'active' })
}

export async function advanceStage(sessionId: string, stage: Stage, agent: AgentName) {
  await updateDoc(doc(db, 'sessions', sessionId), {
    currentStage: stage,
    currentAgent: agent,
  })
}

export async function setAiThinking(sessionId: string, thinking: boolean) {
  await updateDoc(doc(db, 'sessions', sessionId), { aiThinking: thinking })
}

export async function completeSession(sessionId: string) {
  await updateDoc(doc(db, 'sessions', sessionId), { status: 'completed' })
}

export async function revertStage(sessionId: string, stage: Stage, agent: AgentName) {
  await updateDoc(doc(db, 'sessions', sessionId), {
    currentStage: stage,
    currentAgent: agent,
  })
}

export async function addArtifact(
  sessionId: string,
  uploadedBy: string,
  fileName: string,
  content: string,
  fileType: string,
) {
  const wordCount = content.split(/\s+/).length
  await addDoc(collection(db, 'sessions', sessionId, 'artifacts'), {
    uploadedBy,
    fileName,
    content,
    fileType,
    wordCount,
    uploadedAt: serverTimestamp(),
  })
}

export function subscribeToArtifacts(
  sessionId: string,
  callback: (artifacts: Artifact[]) => void,
) {
  return onSnapshot(
    collection(db, 'sessions', sessionId, 'artifacts'),
    (snap) => {
      const artifacts = snap.docs.map(d => ({ id: d.id, ...d.data() } as Artifact))
      callback(artifacts)
    },
  )
}

export async function createReflection(
  sessionId: string,
  prompt: string,
  stage: Stage,
): Promise<string> {
  const ref = await addDoc(collection(db, 'sessions', sessionId, 'reflections'), {
    prompt,
    stage,
    responses: {},
    allSubmitted: false,
  })
  return ref.id
}

export async function submitReflection(
  sessionId: string,
  reflectionId: string,
  participantId: string,
  content: string,
  totalParticipants: number,
) {
  const ref = doc(db, 'sessions', sessionId, 'reflections', reflectionId)
  // Transaction so concurrent submitters can't both compute allSubmitted from a stale read
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref)
    if (!snap.exists()) return

    const data = snap.data() as Reflection
    const newResponses = {
      ...data.responses,
      [participantId]: { content, submittedAt: serverTimestamp() },
    }
    tx.update(ref, {
      [`responses.${participantId}`]: { content, submittedAt: serverTimestamp() },
      allSubmitted: Object.keys(newResponses).length >= totalParticipants,
    })
  })
}

export async function forceCompleteReflection(sessionId: string, reflectionId: string) {
  await updateDoc(
    doc(db, 'sessions', sessionId, 'reflections', reflectionId),
    { allSubmitted: true },
  )
}

export function subscribeToReflections(
  sessionId: string,
  callback: (reflections: Reflection[]) => void,
) {
  return onSnapshot(
    collection(db, 'sessions', sessionId, 'reflections'),
    (snap) => {
      const reflections = snap.docs.map(d => ({ id: d.id, ...d.data() } as Reflection))
      callback(reflections)
    },
  )
}

export async function submitFeedback(
  sessionId: string,
  participantId: string,
  displayName: string,
  data: Omit<Feedback, 'participantId' | 'displayName' | 'submittedAt'>,
) {
  await setDoc(doc(db, 'sessions', sessionId, 'feedback', participantId), {
    participantId,
    displayName,
    ...data,
    submittedAt: serverTimestamp(),
  })
}

export function subscribeToFeedback(
  sessionId: string,
  participantId: string,
  callback: (exists: boolean) => void,
) {
  return onSnapshot(doc(db, 'sessions', sessionId, 'feedback', participantId), (snap) => {
    callback(snap.exists())
  })
}
